//@ts-nocheck
import { FC, ReactElement, useState, useLayoutEffect, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import stylesLogin from "../Login/Login.module.css";
import Button from "../../Components/Buttons/Button";
import Head from "../../Components/Head";
import { useGetConfig } from "../../api/config";
import { useRequestPasswordReset } from "../../api/passwordReset";

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Email is required." })
    .email({ message: "Please enter a valid email address." }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword: FC = (): ReactElement => {
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const { data: config } = useGetConfig();
  const { mutateAsync: requestReset, isLoading, isError, error } = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    setFocus,
    reset,
    formState: { errors, isValid },
  } = useForm<ForgotPasswordFormValues>({
    defaultValues: { email: "" },
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onChange",
  });

  // Apply theme variables if stored
  const themes = JSON.parse(localStorage.getItem("theme") || "null");
  useLayoutEffect(() => {
    if (themes && themes.login_background_color_primary) {
      document.documentElement.style.setProperty(
        "--login-bg-color-primary",
        themes.login_background_color_primary
      );
    }
    if (themes && themes.login_button_color) {
      document.documentElement.style.setProperty(
        "--login-button-color",
        themes.login_button_color
      );
    }
  }, [themes]);

  useEffect(() => {
    setFocus("email");
  }, [setFocus]);

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    try {
      await requestReset({ email: values.email.trim() });
      setSubmittedEmail(values.email.trim());
      setIsSubmitted(true);
    } catch (err) {
      // The backend returns 200 even if user not found, so errors here are
      // network/throttling (429) or malformed payload (400)
    }
  };

  const handleResetAnother = () => {
    setIsSubmitted(false);
    reset({ email: "" });
    setTimeout(() => setFocus("email"), 50);
  };

  return (
    <main className={stylesLogin["entire-page"]}>
      <Head
        title={config?.data?.title ? `Forgot Password | ${config?.data?.title}` : "Forgot Password | Web-Twin"}
        favicon={config?.data?.favicon}
      />

      <div className={stylesLogin["login-card"]}>
        {/* Left Column */}
        <div className={stylesLogin["left-column"]}>
          <div
            className={stylesLogin["back-button"]}
            onClick={() => navigate("/login")}
            title="Back to Sign In"
            role="button"
            tabIndex={0}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </div>

          {!isSubmitted ? (
            <>
              <h1 className={stylesLogin["welcome-title"]}>Reset your password</h1>
              <p className={stylesLogin["welcome-subtitle"]}>
                Enter the email address associated with your account, and we'll send you a link to reset your password.
              </p>

              {isError && (
                <div
                  style={{
                    backgroundColor: "rgba(255, 51, 102, 0.1)",
                    border: "1px solid #ff3366",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    color: "#ff6b8b",
                    fontSize: "13px",
                    marginBottom: "20px",
                  }}
                >
                  {error?.response?.data?.message ||
                    error?.response?.data?.email?.[0] ||
                    error?.response?.data?.detail ||
                    "Unable to send reset instructions. Please try again shortly."}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className={stylesLogin["form-container"]}>
                <div className={stylesLogin["input-group"]}>
                  <label htmlFor="email" className={stylesLogin["input-label"]}>
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="name@company.com"
                    autoComplete="email"
                    className={
                      errors.email
                        ? stylesLogin["username-input-error"]
                        : stylesLogin.username
                    }
                    {...register("email")}
                  />
                  {errors.email && (
                    <span className={stylesLogin["username-error"]}>
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <Button
                  value={isLoading ? "Sending reset link..." : "Send reset link"}
                  btnType="submit"
                  className={
                    !isValid || isLoading
                      ? stylesLogin["login-disable-btn"]
                      : stylesLogin["login-btn"]
                  }
                  disabled={!isValid || isLoading}
                />

                <div style={{ marginTop: "24px", textAlign: "center" }}>
                  <Link
                    to="/login"
                    style={{
                      fontSize: "13px",
                      color: "var(--text-muted, #94a3b8)",
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "var(--xr-cyan, #00ccf7)")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted, #94a3b8)")}
                  >
                    Remember your password? Sign in
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "rgba(0, 204, 247, 0.15)",
                  border: "1px solid rgba(0, 204, 247, 0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#00ccf7",
                  marginBottom: "20px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>

              <h1 className={stylesLogin["welcome-title"]}>Check your email</h1>
              <p className={stylesLogin["welcome-subtitle"]} style={{ marginBottom: "20px" }}>
                If an account is registered with this email address, you'll receive instructions to reset your password shortly.
              </p>

              <div
                style={{
                  background: "rgba(3, 7, 18, 0.6)",
                  border: "1px solid var(--card-border)",
                  borderRadius: "10px",
                  padding: "16px",
                  fontSize: "13px",
                  color: "#94a3b8",
                  lineHeight: "1.6",
                  marginBottom: "28px",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <p style={{ margin: "0 0 8px 0" }}>
                  <strong style={{ color: "#e2e8f0" }}>What happens next?</strong>
                </p>
                <ul style={{ margin: 0, paddingLeft: "18px" }}>
                  <li>Open the link in your email.</li>
                  <li>Choose your new password within 30 minutes.</li>
                  <li>Return to sign in when you're done.</li>
                </ul>
              </div>

              <Button
                value="Return to sign in"
                btnType="button"
                onClick={() => navigate("/login")}
                className={stylesLogin["login-btn"]}
              />

              <button
                type="button"
                onClick={handleResetAnother}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--xr-cyan, #00ccf7)",
                  fontSize: "13px",
                  cursor: "pointer",
                  marginTop: "16px",
                  alignSelf: "center",
                  textDecoration: "underline",
                }}
              >
                Use a different email address
              </button>
            </div>
          )}
        </div>

        {/* Vertical Divider */}
        <div className={stylesLogin["divider"]}></div>

        {/* Right Column */}
        <div className={stylesLogin["right-column"]}>
          <h2 className={stylesLogin["feature-title"]}>A quick note</h2>

          <div className={stylesLogin["feature-list"]} style={{ marginTop: "20px" }}>
            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </div>
              <span>Check your inbox for the reset link.</span>
            </div>

            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
              </div>
              <span>The link expires after 30 minutes.</span>
            </div>

            <div className={stylesLogin["feature-item"]}>
              <div className={stylesLogin["feature-icon"]}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <span>Check your spam folder if the email doesn't arrive.</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ForgotPassword;
